import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

type Props = {
  pricePerPerson: number;
  tourTitle: string;
  pricingTiers?: { maxPax: number; price: number }[];
};

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const DAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];

const formatPrice = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const BookingSidebar = ({ pricePerPerson, tourTitle, pricingTiers }: Props) => {
  const navigate = useNavigate();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [participants, setParticipants] = useState(1);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selected, setSelected] = useState<Date | null>(null);

  const grid = useMemo(() => {
    const first = new Date(viewYear, viewMonth, 1);
    const lastDay = new Date(viewYear, viewMonth + 1, 0).getDate();
    const lead = (first.getDay() + 6) % 7;
    const cells: (Date | null)[] = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let d = 1; d <= lastDay; d++) cells.push(new Date(viewYear, viewMonth, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [viewYear, viewMonth]);

  const effectiveRate = useMemo(() => {
    if (!pricingTiers || pricingTiers.length === 0) return pricePerPerson;
    const sorted = [...pricingTiers].sort((a, b) => a.maxPax - b.maxPax);
    const tier = sorted.find((t) => participants <= t.maxPax);
    return tier ? tier.price : sorted[sorted.length - 1].price;
  }, [participants, pricingTiers, pricePerPerson]);

  const total = effectiveRate;

  const goPrev = () => {
    const m = viewMonth - 1;
    if (m < 0) { setViewMonth(11); setViewYear(viewYear - 1); } else setViewMonth(m);
  };
  const goNext = () => {
    const m = viewMonth + 1;
    if (m > 11) { setViewMonth(0); setViewYear(viewYear + 1); } else setViewMonth(m);
  };

  const [mobileOpen, setMobileOpen] = useState(false);

  const panelContent = (
    <>
      <p className="text-[10px] uppercase tracking-[0.3em] text-primary mb-3">— Book This Tour</p>

      

      {/* Participants */}
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.25em] text-foreground mb-3">​</p>
        <div className="flex items-center justify-between border-b border-border pb-3">
          <span className="text-xs text-muted-foreground uppercase tracking-[0.2em]">Standard</span>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setParticipants((p) => Math.max(1, p - 1))}
              aria-label="Decrease"
              className="h-7 w-7 rounded-full border border-border flex items-center justify-center text-foreground hover:border-primary hover:text-primary transition-colors text-sm"
            >−</button>
            <span className="font-display text-base tabular-nums w-5 text-center">{participants}</span>
            <button
              onClick={() => setParticipants((p) => Math.min(20, p + 1))}
              aria-label="Increase"
              className="h-7 w-7 rounded-full border border-border flex items-center justify-center text-foreground hover:border-primary hover:text-primary transition-colors text-sm"
            >+</button>
          </div>
        </div>
      </div>

      {/* Calendar */}
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.25em] text-foreground mb-3">Choose a date</p>
        <div className="flex items-center justify-between mb-3">
          <button onClick={goPrev} aria-label="Previous month" className="h-7 w-7 rounded-full border border-border flex items-center justify-center text-foreground hover:border-primary hover:text-primary transition-colors text-sm">‹</button>
          <p className="font-display text-sm tracking-wide">{MONTHS[viewMonth]} {viewYear}</p>
          <button onClick={goNext} aria-label="Next month" className="h-7 w-7 rounded-full border border-border flex items-center justify-center text-foreground hover:border-primary hover:text-primary transition-colors text-sm">›</button>
        </div>

        <div className="grid grid-cols-7 text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">
          {DAY_LABELS.map((d, i) => (<div key={i} className="text-center py-1">{d}</div>))}
        </div>

        <div className="grid grid-cols-7 gap-y-px">
          {grid.map((cell, idx) => {
            if (!cell) return <div key={idx} />;
            const past = cell < today;
            const isSel = selected && cell.toDateString() === selected.toDateString();
            return (
              <button
                key={idx}
                onClick={() => !past && setSelected(cell)}
                disabled={past}
                title={!past ? formatPrice(effectiveRate) : undefined}
                className={`relative flex items-center justify-center py-3 transition-colors text-base ${
                  past
                    ? "text-muted-foreground/40 cursor-not-allowed"
                    : isSel
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground hover:bg-background"
                }`}
              >
                <span className="font-display tabular-nums text-lg">{cell.getDate()}</span>
                {!past && (
                  <span className="absolute top-0 right-0 w-0 h-0 border-t-[5px] border-l-[5px] border-t-primary/60 border-l-transparent" />
                )}
              </button>
            );
          })}
        </div>
        <p className="mt-3 text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
          {formatPrice(effectiveRate / participants)} / pax · {participants} pax
        </p>
      </div>

      {/* Summary */}
      <div className="border-t border-border pt-4">
        <p className="text-[10px] uppercase tracking-[0.3em] text-primary mb-3">— Booking Summary</p>
        <ul className="space-y-2 text-xs font-light">
          <li className="flex justify-between gap-3">
            <span className="text-muted-foreground">Date</span>
            <span className="text-right">
              {selected ? selected.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" }) : "Not selected"}
            </span>
          </li>
          <li className="flex justify-between gap-3">
            <span className="text-muted-foreground">Participants</span>
            <span className="tabular-nums">{participants}</span>
          </li>
          <li className="flex justify-between gap-3">
            <span className="text-muted-foreground">Rate / pax</span>
            <span className="tabular-nums">{formatPrice(effectiveRate / participants)}</span>
          </li>
        </ul>
        <div className="mt-4 border-t border-border pt-3 flex items-baseline justify-between">
          <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Total</span>
          <span className="font-display text-2xl tabular-nums">{formatPrice(total)}</span>
        </div>
        {(() => {
          const phone = "85511944544";
          const email = "ladongnan@gmail.com";
          const dateStr = selected
            ? selected.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" })
            : "Not selected";
          const action = selected ? "book" : "inquire about";
          const bodyText = `Hello Aṅkor Sojourns, I'd like to ${action} the "${tourTitle}" tour.\n\nDate: ${dateStr}\nParticipants: ${participants}\nRate: ${formatPrice(effectiveRate)} / pax\nTotal: ${formatPrice(total)}`;
          const waHref = `https://wa.me/${phone}?text=${encodeURIComponent(bodyText)}`;
          const subject = encodeURIComponent(
            `${selected ? "Booking" : "Inquiry"}: ${tourTitle}`
          );
          const mailHref = `mailto:${email}?subject=${subject}&body=${encodeURIComponent(bodyText)}`;
          return (
            <div className="mt-5 grid grid-cols-2 gap-2">
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-center bg-[#25D366] text-white py-3 text-[10px] uppercase tracking-[0.25em] hover:opacity-90 transition-opacity"
              >
                WhatsApp →
              </a>
              <a
                href="/#contact"
                onClick={(e) => {
                  e.preventDefault();
                  const isoDate = selected
                    ? `${selected.getFullYear()}-${String(selected.getMonth() + 1).padStart(2, "0")}-${String(selected.getDate()).padStart(2, "0")}`
                    : "";
                  sessionStorage.setItem(
                    "bookingPrefill",
                    JSON.stringify({
                      tour: tourTitle,
                      date: isoDate,
                      guests: participants,
                      rate: effectiveRate,
                    })
                  );
                  const scrollAndFocus = () => {
                    const contact = document.getElementById("contact");
                    if (!contact) return;
                    contact.scrollIntoView({ behavior: "smooth", block: "start" });
                    window.setTimeout(() => {
                      const firstField = contact.querySelector<HTMLInputElement>(
                        'input, select, textarea'
                      );
                      firstField?.focus({ preventScroll: true });
                    }, 700);
                  };
                  if (window.location.pathname === "/") {
                    window.dispatchEvent(new Event("bookingPrefillUpdated"));
                    scrollAndFocus();
                  } else {
                    sessionStorage.setItem("scrollToContact", "1");
                    navigate("/");
                  }
                }}
                className="block text-center bg-foreground text-background py-3 text-[10px] uppercase tracking-[0.25em] hover:bg-primary transition-colors"
              >
                BOOK NOW
              </a>
            </div>
          );
        })()}
      </div>
    </>
  );

  return (
    <>
      <aside className="hidden lg:block lg:sticky lg:top-8 bg-secondary p-6 md:p-7 shadow-soft">
        {panelContent}
      </aside>

      {/* Mobile floating button + sheet */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetTrigger asChild>
          <button
            className="lg:hidden fixed bottom-4 left-4 right-4 z-40 bg-primary text-primary-foreground py-4 text-xs uppercase tracking-[0.25em] shadow-lg hover:opacity-90 transition-opacity"
            aria-label="Book This Tour"
          >
            Book This Tour →
          </button>
        </SheetTrigger>
        <SheetContent side="bottom" className="bg-secondary p-6 max-h-[90vh] overflow-y-auto rounded-t-2xl">
          {panelContent}
        </SheetContent>
      </Sheet>
    </>
  );
};

export default BookingSidebar;
